import axios from 'axios';

import { fetchCsrfToken } from '~/api/csrf';
import AuthenticationStore from '~/auth/authenticationStore';
import {
  refreshAccessToken,
  shouldRefreshAccessToken,
} from '~/auth/refreshAccessToken';
import { with401Retry } from '~/auth/with401Retry';
import { getAxiosErrorMessage } from '~/common/errors/getAxiosErrorMessage';
import { getEnvVariable } from '~/dev/utils/getEnvVariable';
import { showToast } from '~/eds/src/components/Toast/showToast';
import { LOGIN_CONSTANTS } from '~/features/login/constants';
import { DEFAULT_LANGUAGE, LANGUAGE_KEY, LOCALE_KEY } from '~/i18n/constants';
import { StorageService } from '~/services/StorageService/StorageService';

const getErrorMessage = (error, config) => {
  if (typeof config.showErrorToast === 'string') {
    return config.showErrorToast;
  }
  return getAxiosErrorMessage(error);
};

const defaultErrorHandler = (error) => {
  if (error.config?.showErrorToast) {
    const message = getErrorMessage(error, error.config);

    // Don't show toast for cancelled requests or 401s (handled elsewhere)
    if (!axios.isCancel(error) && error.response?.status !== 401) {
      showToast({ status: 'danger', message });
    }
  }

  return Promise.reject(error);
};

/**
 * Creates a service abstraction around the AxiosInstance.  It currently only exposes common axios HTTP methods, but this can be expanded in the future.
 * @param {*} configuration: simple object containing just the URL.  Can be made more configurable in the future.
 */
export default function createService(configuration) {
  const instance = axios.create({
    baseURL: configuration.url,
    timeout: 120000,
    allowAbsoluteUrls: false,
    withCredentials: getEnvVariable('REACT_APP_CORS_REQUESTS') === 'true',
  });

  const AS = AuthenticationStore();
  const wasLoggedIn = AS.isLoggedIn();

  instance.interceptors.request.use(async (config) => {
    // do something before request is sent
    config.headers = {
      ...config.headers,
      'Accept-Language':
        StorageService.getItem(LOCALE_KEY) ||
        StorageService.getItem(LANGUAGE_KEY) ||
        DEFAULT_LANGUAGE,
      'User-Timezone':
        new Intl.DateTimeFormat().resolvedOptions().timeZone ||
        'America/Vancouver',
    };

    // Fetch and set up CSRF token
    if (wasLoggedIn && !AS.getCsrf()) {
      await fetchCsrfToken();
    }

    if (AS.getCsrf()) {
      config.headers[LOGIN_CONSTANTS.HEADERS.CSRF_TOKEN] = AS.getCsrf();
    }

    if (wasLoggedIn && shouldRefreshAccessToken()) {
      await refreshAccessToken();
    }

    if (!AS.getAccessToken() || !AS.getRefreshToken()) {
      // for httponly support. No longer need access token or refresh token
      return config;
    }

    // Only set auth token if it exists and caller has not set an explicit Authorization header
    if (
      !config.headers.Authorization &&
      (AS.getAccessToken() || AS.getRefreshToken())
    ) {
      config.headers.Authorization = configuration.refresh_token
        ? `Bearer ${AS.getRefreshToken()}`
        : `Bearer ${AS.getAccessToken()}`;
    }

    return config;
  });

  instance.interceptors.response.use((response) => {
    const { config, data } = response;

    if (config.showSuccessToast) {
      const message =
        typeof config.showSuccessToast === 'string'
          ? config.showSuccessToast
          : data.message;

      showToast({ status: 'success', message });
    }

    return config.fullResponse ? response : data;
  }, configuration.errorHandler || defaultErrorHandler);

  if (configuration.shouldRetryOn401) {
    return {
      get: with401Retry(instance.get.bind(instance)),
      post: with401Retry(instance.post.bind(instance)),
      remove: with401Retry(instance.delete.bind(instance)),
      put: with401Retry(instance.put.bind(instance)),
      patch: with401Retry(instance.patch.bind(instance)),
      concurrentRequests: with401Retry(axios.all.bind(axios)),
    };
  }

  return {
    get: instance.get,
    post: instance.post,
    remove: instance.delete,
    put: instance.put,
    patch: instance.patch,
    concurrentRequests: axios.all,
  };
}
